import type { AuthMethod } from '@/stores/authStore';

/** LiST-only vs LiST + IdP (Entra / Shibboleth) session teardown. */
export type LogoutScope = 'list' | 'full';

export type LogoutOptions = {
  scope?: LogoutScope;
  /** Navigate to home after LiST sign-out when the page stays loaded. Default true. */
  redirectToHome?: boolean;
};

/**
 * Next.js public URL prefix. When `basePath` includes a trailing `/dotnet` segment that is
 * API-only behind the reverse proxy, strip it so browser navigation stays on the UI vhost path.
 */
export function resolveFrontendBasePath(basePath: string): string {
  let path = (basePath || '').replace(/\/$/, '');
  if (path.endsWith('/dotnet')) {
    path = path.slice(0, -'/dotnet'.length);
  }
  return path;
}

/** App-relative home path on the frontend (not the API `/dotnet` prefix). */
export function resolveHomePath(basePath: string): string {
  const frontend = resolveFrontendBasePath(basePath);
  return frontend ? `${frontend}/` : '/';
}

/** Full URL for post-logout landing (Home) — used by MSAL logoutRedirect. */
export function resolveHomeUrl(basePath: string): string {
  if (typeof window === 'undefined') {
    return resolveHomePath(basePath);
  }
  return `${window.location.origin}${resolveHomePath(basePath)}`;
}

export function navigateToHome(basePath: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.location.assign(resolveHomePath(basePath));
}

/**
 * Path-only return target for `/Shibboleth.sso/Logout` (matches sign-in `target` style).
 * PSU SP forwards `/` or `/list/` to the Next app; `/list/dotnet/` is API-only and must not be used.
 */
export function resolveShibbolethLogoutReturnPath(basePath: string): string {
  return resolveHomePath(basePath);
}

/**
 * Shibboleth SP logout at the web server root (not under the LiST API base path).
 * Uses a path-only `return` query param, not a full URL.
 */
export function buildShibbolethLogoutUrl(returnPath: string): string {
  const params = new URLSearchParams();
  params.set('return', returnPath);
  return `${window.location.origin}/Shibboleth.sso/Logout?${params.toString()}`;
}

/** Whether full SSO sign-out can target a known IdP for this session. */
export function canSignOutOfIdp(authMethod?: AuthMethod): boolean {
  return authMethod === 'msal' || authMethod === 'shibboleth';
}
